// client/src/pages/School_Admin/Facilities/InventoryControlPage.js
import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputLabel, FormControl, Select, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Dialog, DialogContent, DialogTitle, DialogActions,
  Chip, Snackbar, Alert, Paper, Grid, IconButton, Tooltip, InputAdornment,
  Card, CardContent, LinearProgress, Divider
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, Inventory as InventoryIcon,
  Warning as WarningIcon, AddCircle as AddCircleIcon, RemoveCircle as RemoveCircleIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';

const CATEGORY_OPTIONS = [
  { value: 'Instructional Materials & Supplies', labelKey: 'inventoryControlPage.categories.instructionalMaterials' },
  { value: 'Technology & Equipment', labelKey: 'inventoryControlPage.categories.technologyEquipment' },
  { value: 'Furniture & Fixtures', labelKey: 'inventoryControlPage.categories.furnitureFixtures' },
  { value: 'Cafeteria / Food Service', labelKey: 'inventoryControlPage.categories.cafeteriaFoodService' },
  { value: 'Administrative & Office Supplies', labelKey: 'inventoryControlPage.categories.administrativeOfficeSupplies' },
  { value: 'Maintenance & Operations Supplies', labelKey: 'inventoryControlPage.categories.maintenanceOperationsSupplies' },
  { value: 'Student/Staff Apparel', labelKey: 'inventoryControlPage.categories.studentStaffApparel' },
  { value: 'Transportation Supplies', labelKey: 'inventoryControlPage.categories.transportationSupplies' },
  { value: 'Health & Safety Supplies', labelKey: 'inventoryControlPage.categories.healthSafetySupplies' },
  { value: 'Others', labelKey: 'inventoryControlPage.categories.others' }
];

const emptyForm = {
  name: '', category: '', description: '', unit: '',
  purchaseCost: '', purchaseDate: '', usefulLifeYears: '',
  minStock: '', currentStock: ''
};

const InventoryControlPage = () => {
  const { t } = useTranslation();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [adjustDialog, setAdjustDialog] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchInventory = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchJson('/facilities/inventory', { method: 'GET' });
      const raw = Array.isArray(data) ? data : (data.inventory || data.data || []);
      const normalized = raw.map((item) => ({
        ...item,
        currentStock: item.currentStock ?? item.quantity ?? 0,
      }));
      setInventory(normalized);
    } catch (error) {
      setSnackbar({ open: true, message: t('inventoryControlPage.snack.fetchFailed'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  // Listen for phase/academic year changes
  useEffect(() => {
    const refresh = () => fetchInventory();
    window.addEventListener('app:ay:changed', refresh);
    window.addEventListener('app:phase:changed', refresh);
    return () => {
      window.removeEventListener('app:ay:changed', refresh);
      window.removeEventListener('app:phase:changed', refresh);
    };
  }, [fetchInventory]);

  const openCreateDialog = () => {
    setEditingId(null);
    setFormData({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.name || '',
      category: item.category || '',
      description: item.description || '',
      unit: item.unit || '',
      purchaseCost: item.purchaseCost || '',
      purchaseDate: item.purchaseDate ? item.purchaseDate.slice(0, 10) : '',
      usefulLifeYears: item.usefulLifeYears || '',
      minStock: item.minStock ?? '',
      currentStock: item.currentStock ?? ''
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!String(formData.name || '').trim()) {
      setSnackbar({ open: true, message: t('inventoryControlPage.snack.nameRequired'), severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        category: formData.category || null,
        description: formData.description || null,
        unit: formData.unit || null,
        purchaseCost: formData.purchaseCost ? Number(formData.purchaseCost) : 0,
        purchaseDate: formData.purchaseDate || null,
        usefulLifeYears: formData.usefulLifeYears ? Number(formData.usefulLifeYears) : null,
        minStock: formData.minStock !== '' ? Number(formData.minStock) : 0,
        currentStock: formData.currentStock !== '' ? Number(formData.currentStock) : 0
      };
      if (editingId) {
        await fetchJson(`/facilities/inventory/${editingId}`, { method: 'PUT', body: payload });
        setSnackbar({ open: true, message: t('inventoryControlPage.snack.updated'), severity: 'success' });
      } else {
        await fetchJson('/facilities/inventory', { method: 'POST', body: payload });
        setSnackbar({ open: true, message: t('inventoryControlPage.snack.created'), severity: 'success' });
      }
      setDialogOpen(false);
      fetchInventory();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || t('inventoryControlPage.snack.saveFailed'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await fetchJson(`/facilities/inventory/${deleteConfirm.id}`, { method: 'DELETE' });
      setSnackbar({ open: true, message: t('inventoryControlPage.snack.deleted'), severity: 'success' });
      setDeleteConfirm(null);
      fetchInventory();
    } catch (error) {
      setSnackbar({ open: true, message: t('inventoryControlPage.snack.deleteFailed'), severity: 'error' });
    }
  };

  const handleStockAdjust = async (direction) => {
    if (!adjustDialog || !adjustQty || isNaN(Number(adjustQty))) return;
    const qty = Number(adjustQty);
    const newStock = direction === 'add'
      ? Number(adjustDialog.currentStock) + qty
      : Math.max(0, Number(adjustDialog.currentStock) - qty);
    try {
      await fetchJson(`/facilities/inventory/${adjustDialog.id}`, { method: 'PUT', body: { currentStock: newStock } });
      const msg = direction === 'add'
        ? t('inventoryControlPage.snack.stockIncreased', { stock: newStock })
        : t('inventoryControlPage.snack.stockDecreased', { stock: newStock });
      setSnackbar({ open: true, message: msg, severity: 'success' });
      setAdjustDialog(null);
      setAdjustQty('');
      fetchInventory();
    } catch (error) {
      setSnackbar({ open: true, message: t('inventoryControlPage.snack.adjustFailed'), severity: 'error' });
    }
  };

  // Client-side filtering
  const filtered = inventory.filter(item => {
    if (filterCategory && item.category !== filterCategory) return false;
    if (filterStatus === 'low' && Number(item.currentStock) > Number(item.minStock)) return false;
    if (filterStatus === 'ok' && Number(item.currentStock) <= Number(item.minStock)) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (item.name || '').toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q) ||
        (item.category || '').toLowerCase().includes(q);
    }
    return true;
  });

  const lowStockItems = inventory.filter(i => Number(i.currentStock) <= Number(i.minStock) && Number(i.minStock) > 0);
  const totalValue = inventory.reduce((sum, i) => sum + (Number(i.purchaseCost) || 0) * (Number(i.currentStock) || 0), 0);
  const lowStockPreview = lowStockItems.slice(0, 5).map(i => i.name).join(', ');
  const lowStockSuffix = lowStockItems.length > 5 ? '...' : '';

  const getCategoryLabel = (value) => {
    const opt = CATEGORY_OPTIONS.find(o => o.value === value);
    return opt ? t(opt.labelKey) : (value || '');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <InventoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">{t('inventoryControlPage.title')}</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>{t('inventoryControlPage.buttons.addItem')}</Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">{t('inventoryControlPage.summary.totalItems')}</Typography>
            <Typography variant="h4" fontWeight="bold">{inventory.length}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">{t('inventoryControlPage.summary.lowStock')}</Typography>
            <Typography variant="h4" fontWeight="bold" color="error.main">{lowStockItems.length}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">{t('inventoryControlPage.summary.categories')}</Typography>
            <Typography variant="h4" fontWeight="bold">{new Set(inventory.map(i => i.category).filter(Boolean)).size}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">{t('inventoryControlPage.summary.totalValue')}</Typography>
            <Typography variant="h4" fontWeight="bold">{totalValue.toLocaleString()} FCFA</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Low stock alert banner */}
      {lowStockItems.length > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <strong>{t('inventoryControlPage.alert.lowStockCount', { count: lowStockItems.length })}</strong> {t('inventoryControlPage.alert.lowStockText', { items: lowStockPreview, suffix: lowStockSuffix })}
        </Alert>
      )}

      {/* Search & Filter Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small" placeholder={t('inventoryControlPage.search.placeholder')}
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('inventoryControlPage.filters.category')}</InputLabel>
              <Select label={t('inventoryControlPage.filters.category')} value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <MenuItem value="">{t('inventoryControlPage.filters.allCategories')}</MenuItem>
                {CATEGORY_OPTIONS.map(({ value, labelKey }) => <MenuItem key={value} value={value}>{t(labelKey)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('inventoryControlPage.filters.stockStatus')}</InputLabel>
              <Select label={t('inventoryControlPage.filters.stockStatus')} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <MenuItem value="">{t('inventoryControlPage.filters.all')}</MenuItem>
                <MenuItem value="ok">{t('inventoryControlPage.filters.inStock')}</MenuItem>
                <MenuItem value="low">{t('inventoryControlPage.filters.lowStock')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" size="small" onClick={() => { setSearchQuery(''); setFilterCategory(''); setFilterStatus(''); }}>
              {t('inventoryControlPage.buttons.clearFilters')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Inventory Table */}
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell><b>{t('inventoryControlPage.table.name')}</b></TableCell>
              <TableCell><b>{t('inventoryControlPage.table.category')}</b></TableCell>
              <TableCell><b>{t('inventoryControlPage.table.unit')}</b></TableCell>
              <TableCell align="center"><b>{t('inventoryControlPage.table.minStock')}</b></TableCell>
              <TableCell align="center"><b>{t('inventoryControlPage.table.currentStock')}</b></TableCell>
              <TableCell align="right"><b>{t('inventoryControlPage.table.cost')}</b></TableCell>
              <TableCell align="center"><b>{t('inventoryControlPage.table.status')}</b></TableCell>
              <TableCell align="center"><b>{t('inventoryControlPage.table.actions')}</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center"><Typography color="text.secondary" py={3}>{t('inventoryControlPage.table.noItems')}</Typography></TableCell></TableRow>
            ) : filtered.map((item) => {
              const isLow = Number(item.currentStock) <= Number(item.minStock) && Number(item.minStock) > 0;
              return (
                <TableRow key={item.id} sx={isLow ? { bgcolor: 'error.50' } : {}}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{item.name}</Typography>
                    {item.description && <Typography variant="caption" color="text.secondary">{item.description}</Typography>}
                  </TableCell>
                  <TableCell><Typography variant="body2">{item.category ? getCategoryLabel(item.category) : '-'}</Typography></TableCell>
                  <TableCell>{item.unit || '-'}</TableCell>
                  <TableCell align="center">{item.minStock ?? '-'}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="bold" color={isLow ? 'error.main' : 'text.primary'}>
                      {item.currentStock ?? 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{Number(item.purchaseCost) ? `${Number(item.purchaseCost).toLocaleString()} FCFA` : '-'}</TableCell>
                  <TableCell align="center">
                    <Chip label={isLow ? t('inventoryControlPage.statuses.lowStock') : t('inventoryControlPage.statuses.inStock')} color={isLow ? 'error' : 'success'} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <Tooltip title={t('inventoryControlPage.tooltips.addStock')}>
                        <IconButton size="small" color="success" onClick={() => { setAdjustDialog(item); setAdjustQty(''); }}>
                          <AddCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('inventoryControlPage.tooltips.edit')}>
                        <IconButton size="small" color="primary" onClick={() => openEditDialog(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('inventoryControlPage.tooltips.delete')}>
                        <IconButton size="small" color="error" onClick={() => setDeleteConfirm(item)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('inventoryControlPage.dialogs.form.editTitle') : t('inventoryControlPage.dialogs.form.addTitle')}</DialogTitle>
        <DialogContent>
          <Box component="form" id="inv-form" onSubmit={handleSave} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label={t('inventoryControlPage.dialogs.form.itemName')} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required fullWidth />
              </Grid>
              <Grid item xs={12}>
                <TextField label={t('inventoryControlPage.dialogs.form.description')} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth multiline rows={2} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('inventoryControlPage.dialogs.form.category')}</InputLabel>
                  <Select label={t('inventoryControlPage.dialogs.form.category')} value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                    <MenuItem value=""><em>{t('inventoryControlPage.dialogs.form.none')}</em></MenuItem>
                    {CATEGORY_OPTIONS.map(({ value, labelKey }) => <MenuItem key={value} value={value}>{t(labelKey)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label={t('inventoryControlPage.dialogs.form.unit')} value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} fullWidth />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField label={t('inventoryControlPage.dialogs.form.minStock')} type="number" value={formData.minStock} onChange={(e) => setFormData({ ...formData, minStock: e.target.value })} fullWidth />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField label={t('inventoryControlPage.dialogs.form.currentStock')} type="number" value={formData.currentStock} onChange={(e) => setFormData({ ...formData, currentStock: e.target.value })} fullWidth />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label={t('inventoryControlPage.dialogs.form.purchaseCost')} type="number" value={formData.purchaseCost} onChange={(e) => setFormData({ ...formData, purchaseCost: e.target.value })} fullWidth />
              </Grid>
              <Grid item xs={6}>
                <TextField label={t('inventoryControlPage.dialogs.form.purchaseDate')} type="date" InputLabelProps={{ shrink: true }} value={formData.purchaseDate} onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })} fullWidth />
              </Grid>
              <Grid item xs={6}>
                <TextField label={t('inventoryControlPage.dialogs.form.usefulLifeYears')} type="number" value={formData.usefulLifeYears} onChange={(e) => setFormData({ ...formData, usefulLifeYears: e.target.value })} fullWidth />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="outlined">{t('inventoryControlPage.buttons.cancel')}</Button>
          <Button type="submit" form="inv-form" variant="contained" disabled={saving}>{saving ? t('inventoryControlPage.buttons.saving') : (editingId ? t('inventoryControlPage.buttons.update') : t('inventoryControlPage.buttons.create'))}</Button>
        </DialogActions>
      </Dialog>

      {/* Stock Adjustment Dialog */}
      <Dialog open={!!adjustDialog} onClose={() => setAdjustDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('inventoryControlPage.dialogs.adjust.title', { name: adjustDialog?.name })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>{t('inventoryControlPage.dialogs.adjust.currentStockLabel')} <b>{adjustDialog?.currentStock ?? 0}</b></Typography>
          <TextField label={t('inventoryControlPage.dialogs.adjust.quantity')} type="number" value={adjustQty} onChange={(e) => setAdjustQty(e.target.value)} fullWidth autoFocus inputProps={{ min: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialog(null)} variant="outlined">{t('inventoryControlPage.buttons.cancel')}</Button>
          <Button variant="contained" color="error" startIcon={<RemoveCircleIcon />} disabled={!adjustQty || Number(adjustQty) <= 0} onClick={() => handleStockAdjust('remove')}>{t('inventoryControlPage.buttons.remove')}</Button>
          <Button variant="contained" color="success" startIcon={<AddCircleIcon />} disabled={!adjustQty || Number(adjustQty) <= 0} onClick={() => handleStockAdjust('add')}>{t('inventoryControlPage.buttons.add')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>{t('inventoryControlPage.dialogs.delete.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('inventoryControlPage.dialogs.delete.prompt')} <b>{deleteConfirm?.name}</b>? {t('inventoryControlPage.dialogs.delete.cannotUndo')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} variant="outlined">{t('inventoryControlPage.buttons.cancel')}</Button>
          <Button onClick={handleDelete} variant="contained" color="error">{t('inventoryControlPage.buttons.delete')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryControlPage;
